/**
 * CountryLandingPage - Dynamic page that loads country config from URL
 * Supports both English and translated slugs via resolveCountrySlug.
 */
import { useLocation, Navigate } from 'react-router-dom';
import { COUNTRY_PAGES } from '@/data/country-landing-pages';
import { resolveCountrySlug } from '@/data/country-slug-translations';
import { getCountrySEO } from '@/data/country-seo-i18n';
import { CountryLandingPageTemplate } from '@/components/templates/CountryLandingPageTemplate';
import { useLanguage } from '@/contexts/LanguageContextNew';
import { useMemo } from 'react';

export default function CountryLandingPage() {
  const location = useLocation();
  const { language } = useLanguage();
  
  // Extract slug from pathname (last segment)
  const pathParts = location.pathname.split('/').filter(Boolean);
  const slug = pathParts[pathParts.length - 1];
  
  // Resolve slug (translated or English) to country key
  const countryKey = resolveCountrySlug(slug, language);
  
  // Find country data by key or by slug fallback
  const resolvedKey = countryKey || Object.keys(COUNTRY_PAGES).find(k => COUNTRY_PAGES[k].slug === slug);
  const countryData = resolvedKey ? COUNTRY_PAGES[resolvedKey] : undefined;
  
  // Apply localized SEO overlay if not English
  const localizedData = useMemo(() => {
    if (!countryData || !resolvedKey || language === 'en') return countryData;
    const seo = getCountrySEO(resolvedKey, language);
    if (!seo) return countryData;
    return {
      ...countryData,
      title: seo.title,
      description: seo.description,
      heroTitle: seo.heroTitle,
      heroSubtitle: seo.heroSubtitle,
    };
  }, [countryData, resolvedKey, language]);
  
  if (!localizedData) {
    return <Navigate to={`/${language}/services/bpo`} replace />;
  }
  
  return <CountryLandingPageTemplate data={localizedData} />;
}
